import { createContext, useContext, useEffect, useState } from "react";
import { api, ensureCsrfToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        await ensureCsrfToken();
        if (active) setUser(data);
      } catch {
        if (active) setUser(false);
      } finally {
        if (active) setReady(true);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });

    if (!data.mfa_required) {
      await ensureCsrfToken();
      setUser(data);
    }

    return data;
  };

  const verifyMfa = async (challengeToken, code) => {
    const { data } = await api.post("/auth/mfa/verify", {
      challenge_token: challengeToken,
      code,
    });

    await ensureCsrfToken();
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await ensureCsrfToken();
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, verifyMfa, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
